import React, { useEffect, useRef, useState } from "react";
import { MdDownload, MdIosShare, MdOpenInNew, MdPrint } from "react-icons/md";

import CircularLoader from "../../../../../../Components/CircularLoader/CircularLoader";
import {
  canShareFiles,
  downloadFile,
  isTouch,
  openFile,
  printFile,
  shareFiles,
} from "./fileActions";

const Option = ({ icon: Icon, label, detail, onClick, primary = false }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-ui-row-hover focus:bg-ui-row-hover focus:outline-none lg:min-h-[44px] [@media(pointer:coarse)]:min-h-[52px]"
  >
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
        primary
          ? "bg-brand text-brand-contrast"
          : "bg-ui-surface-sunken text-ui-text-muted"
      }`}
    >
      <Icon size={17} aria-hidden="true" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-semibold text-ui-text lg:text-[13px]">
        {label}
      </span>
      {detail && (
        <span className="block text-[12px] text-ui-text-subtle lg:text-[11.5px]">
          {detail}
        </span>
      )}
    </span>
  </button>
);

const IconAction = ({ icon: Icon, label, onClick }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-ui-text-muted transition-colors hover:bg-ui-row-hover hover:text-brand lg:h-8 lg:w-8 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
  >
    <Icon size={17} aria-hidden="true" />
  </button>
);

/**
 * The menu under an Order / Pack / Label / Decl button: the file being got
 * ready, then what to do with it — Print, Open, Share, Download. Phones and
 * tablets lead with Share (the system sheet: Print, a printer's app, Files,
 * email…), as a page can't print a file there. `status`: { state:
 * "loading" | "ready" | "error", files }. Taps outside (but in
 * `ignoreRef`, the buttons) and Escape close it.
 */
export default function FileMenu({
  title,
  status,
  onRetry,
  onClose,
  ignoreRef,
}) {
  const menuRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [shareFailed, setShareFailed] = useState(false);

  useEffect(() => {
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (ignoreRef?.current?.contains(event.target)) return;
      closeRef.current();
    };
    const onKey = (event) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [ignoreRef]);

  const state = status?.state ?? "loading";
  const files = state === "ready" ? status.files : [];
  const many = files.length > 1;
  const touch = isTouch();
  const shareable = canShareFiles(files);

  const run = (action) => () => {
    action();
    onClose();
  };
  const share = async () => {
    setShareFailed(false);
    try {
      await shareFiles(files, title);
      onClose();
    } catch (error) {
      // Closing the share sheet isn't a failure.
      if (error?.name === "AbortError") onClose();
      else setShareFailed(true);
    }
  };

  const subtitle =
    state === "loading"
      ? "Getting it ready…"
      : state === "error"
        ? "Couldn't get the file"
        : many
          ? `${files.length} files`
          : files[0]?.name;

  let options = null;
  if (state === "ready" && !many && files[0]) {
    const file = files[0];
    options = touch ? (
      <>
        {shareable && (
          <Option
            primary
            icon={MdIosShare}
            label="Print or open with an app"
            detail="Print, a printer's app, Files, email…"
            onClick={share}
          />
        )}
        <Option
          primary={!shareable}
          icon={MdOpenInNew}
          label="Open"
          detail={
            shareable ? "View it" : "View it, then print or share from there"
          }
          onClick={run(() => openFile(file))}
        />
        <Option
          icon={MdDownload}
          label="Download"
          detail="Save it to this device"
          onClick={run(() => downloadFile(file))}
        />
      </>
    ) : (
      <>
        <Option
          primary
          icon={MdPrint}
          label="Print"
          detail="Straight to the print dialog"
          onClick={run(() => printFile(file))}
        />
        <Option
          icon={MdOpenInNew}
          label="Open"
          detail="In a new tab"
          onClick={run(() => openFile(file))}
        />
        <Option
          icon={MdDownload}
          label="Download"
          detail="Save it to this computer"
          onClick={run(() => downloadFile(file))}
        />
        {shareable && (
          <Option
            icon={MdIosShare}
            label="Share"
            detail="Send it to another app"
            onClick={share}
          />
        )}
      </>
    );
  } else if (state === "ready" && many) {
    options = (
      <>
        <ul className="m-0 max-h-[260px] list-none overflow-y-auto p-0">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-1 border-b border-ui-border-subtle py-1 pl-3 pr-1.5"
            >
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-ui-text">
                {file.name}
              </span>
              <IconAction
                icon={MdPrint}
                label={`Print ${file.name}`}
                onClick={run(() => printFile(file))}
              />
              <IconAction
                icon={MdOpenInNew}
                label={`Open ${file.name}`}
                onClick={run(() => openFile(file))}
              />
              <IconAction
                icon={MdDownload}
                label={`Download ${file.name}`}
                onClick={run(() => downloadFile(file))}
              />
            </li>
          ))}
        </ul>
        {shareable && (
          <Option
            primary={touch}
            icon={MdIosShare}
            label={touch ? "Print or open with an app" : "Share all"}
            detail={
              touch ? "All the files: Print, a printer's app, Files…" : null
            }
            onClick={share}
          />
        )}
        <Option
          icon={MdDownload}
          label="Download all"
          onClick={run(() => files.forEach((file) => downloadFile(file)))}
        />
      </>
    );
  }

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={title}
      className="absolute left-2 right-2 top-full z-30 mt-1 overflow-hidden rounded-lg border border-ui-border bg-ui-surface text-left shadow-ui-md sm:left-auto sm:right-3 sm:w-[320px]"
    >
      <div className="border-b border-ui-border px-3 py-2">
        <div className="text-[13.5px] font-semibold text-ui-text">{title}</div>
        <div className="truncate text-[11.5px] text-ui-text-subtle">
          {subtitle}
        </div>
      </div>

      {state === "loading" && (
        <div className="flex items-center gap-2 px-3 py-4 text-[13px] text-ui-text-muted">
          <CircularLoader size={14} /> Getting the file ready…
        </div>
      )}

      {state === "error" && (
        <div className="px-3 py-3 text-[13px] text-ui-text-muted">
          Couldn't get the file.{" "}
          <button
            type="button"
            onClick={onRetry}
            className="min-h-[40px] font-semibold text-brand-text underline lg:min-h-0"
          >
            Try again
          </button>
        </div>
      )}

      {options}

      {shareFailed && (
        <div className="border-t border-ui-border px-3 py-2 text-[12px] text-state-danger-text">
          Sharing didn't work here: use Open or Download.
        </div>
      )}
    </div>
  );
}
